import React from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Flex } from '@atlaskit/primitives/compiled';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { StatusLozengeButton } from '../../../../../../../common/ui/status-lozenge-button';
import { enrichExcerpt } from '../../../../../../../common/utils/search-result-utils';

import type { searchGraphLead_AtlassianSearchPage$key } from './__generated__/searchGraphLead_AtlassianSearchPage.graphql';
import { messages } from './messages.tsx';

const styles = cssMap({
	leadContainer: {
		width: '100%',
	},
	resultWrapper: {
		flex: '1',
		minWidth: '0px',
	},
});

export const SearchGraphLead = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
	selectedSites,
}: SearchGraphResultProps<searchGraphLead_AtlassianSearchPage$key>): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment searchGraphLead_AtlassianSearchPage on SearchResultGraphDocument
			@argumentDefinitions(
				isLeadNounEnabled: { type: "Boolean!", provider: "is-lead-noun-enabled.relayprovider" }
				isLeadUserAsTitleEnabled: {
					type: "Boolean!"
					provider: "is-lead-user-as-title-enabled.relayprovider"
				}
			) {
				id
				url
				title
				lastModifiedDate
				excerpt
				entity @include(if: $isLeadNounEnabled) {
					... on ExternalLead {
						status
						lastUpdated
						leadUser @include(if: $isLeadUserAsTitleEnabled) {
							thirdPartyUser {
								name
							}
						}
						owners {
							thirdPartyUser {
								name
							}
						}
						createdBy {
							thirdPartyUser {
								name
							}
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { id, url, lastModifiedDate, excerpt, entity, linkedResults: rawLinkedResults } = data;

	const title = fg('rovo_fps_lead_user_as_title')
		? (entity?.leadUser?.thirdPartyUser?.name ?? entity?.owners?.at(0)?.thirdPartyUser?.name)
		: entity?.owners?.at(0)?.thirdPartyUser?.name;

	const ownerName =
		entity?.owners?.[0]?.thirdPartyUser?.name ?? entity?.createdBy?.thirdPartyUser?.name;

	const lastModifiedComponent = (
		<LastModified lastModified={entity?.lastUpdated ?? lastModifiedDate} />
	);

	const description = <DescriptionBody description={enrichExcerpt(excerpt)} />;

	const statusText = entity?.status ?? '';

	const headerMetadataParts = [
		formatMessage(messages.leadLabel),
		['owner', ownerName],
		['last-modified', lastModifiedComponent],
	];

	const linkedResults = rawLinkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const footerComponent = (
		<MentionedByLinkedResults
			linkedResults={linkedResults || []}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
			url={url}
			position={position}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			isAllProductSelected={!selectedProduct}
		/>
	);

	return (
		<Flex gap="space.100" alignItems="start" role="listitem" xcss={styles.leadContainer}>
			<Box xcss={styles.resultWrapper}>
				<SearchResult
					id={id}
					product={product}
					productSelected={!!selectedProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultContextMenu}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					icon={icon}
					description={description}
					headerMetadataParts={headerMetadataParts}
					title={enrichExcerpt(title)}
					role="none"
					url={url}
					isThirdParty
					cloudId={cloudId}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					position={position}
					partial={data}
					appMode={appMode}
					footer={footerComponent}
					hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			</Box>
			{statusText && <StatusLozengeButton statusText={statusText} />}
		</Flex>
	);
};
